import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const FAQ_ITEMS = [
  {
    question: 'Does Rotherham\'s all-GREEN DfT rating block my pothole claim?',
    answer: 'No — but it changes your approach. Rotherham is one of the few councils in England rated GREEN on all four DfT scorecards, which strengthens their Section 58 defence at network level. Your claim still turns on the specific defect: whether it met their intervention criteria, whether it was found in a safety inspection, and whether it was repaired within their Code of Practice timeframes — not on the colour of a borough-wide scorecard.',
  },
  {
    question: 'What does all-GREEN actually mean for Rotherham?',
    answer: 'GREEN means Rotherham performs above the DfT threshold on overall condition, spend, and best practice — A-roads at 85.5% good and B&C roads at 86.5% good versus national averages of roughly 69% and 62%. It does not mean zero potholes: the council filled an estimated 15,936 in 2024/25 alone, and roughly 11% of its 775km unclassified network was still in RED condition in March 2025.',
  },
  {
    question: 'Rotherham still repairs 15,000+ potholes a year — can I still claim?',
    answer: 'Yes. Reactive repairs are evidence that defects form on this network regardless of a GREEN rating. Rotherham\'s own transparency report shows 92,466 estimated pothole repairs across five years (2020/21–2024/25). A council filling potholes daily is a council where individual defects can still damage vehicles between inspections — especially if yours was reportable and left unrepaired.',
  },
  {
    question: 'What if my pothole was on an estate or unclassified road?',
    answer: 'Unclassified roads make up 775km — 65% of Rotherham\'s 1,191km carriageway network. Condition here has improved markedly (RED share fell from 18.89% in 2020/21 to 10.93% in 2024/25), but the council only coarse-visually inspects 25% of U-roads each year — roughly 195km on a four-year return cycle. Section 58 turns on whether your specific road was inspected and whether the defect should have been caught, not on borough-wide improvement trends.',
  },
  {
    question: 'How does Section 58 apply to one of England\'s best-rated councils?',
    answer: 'Section 58 of the Highways Act 1980 lets a council defend a claim if it proves reasonable maintenance of the specific highway. Rotherham\'s GREEN ratings, 90%+ preventative spend, and ISO-aligned asset management make that defence stronger than most. But Section 41 still imposes a duty to maintain — and their own data shows thousands of reactive repairs, A-road RED condition edging up, and U-roads surveyed on a four-year CVI cycle. Prior reports, photos showing defect age, and proof the pothole met their intervention criteria remain your strongest tools.',
  },
];

export function RotherhamCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Rotherham Pothole Claims | All-GREEN DfT Rating | Fixtyer"
        description="Rotherham is one of England's few all-GREEN councils — yet filled 15,936 potholes in 2024/25. See what their transparency report reveals about estate roads, Section 58, and your claim."
        canonical="/councils/rotherham"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Rotherham', url: '/councils/rotherham' },
        ]}
      />
      <FAQSchema faqs={FAQ_ITEMS} />
      <ServiceSchema
        councilName="Rotherham Metropolitan Borough Council"
        councilSlug="rotherham"
        rating="green"
      />
      <SpeakableSchema
        url="/councils/rotherham"
        headline="Rotherham Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Rotherham</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="green" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Rotherham: All-GREEN, Still 15,936 Pothole Repairs
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Rotherham Metropolitan Borough Council is one of the very few authorities in England rated{' '}
            <strong className="text-emerald-400">GREEN on all four DfT scorecards</strong> — condition, spend,
            and best practice included. Their own transparency report still records{' '}
            <strong className="text-amber-400">15,936 pothole repairs in 2024/25</strong>, a 775km unclassified
            network on a four-year visual survey cycle, and A-road RED condition edging up. GREEN is a network
            verdict. Your claim lives or dies on the specific defect and Section 58.
          </p>

          <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-emerald-400">15,936</div>
            <div className="text-white font-medium mt-2">Estimated potholes filled in 2024/25</div>
            <div className="text-brand-300 text-sm mt-1">
              At an all-GREEN council managing 1,191km of roads — roughly 44 reactive repairs every day,
              despite 89.63% of maintenance spend classed as preventative.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* DfT Scorecard */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-8">
            The DfT's Verdict — All Four GREEN
          </h2>

          <div className="bg-surface-50 rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full">
              <caption className="sr-only">Rotherham Metropolitan Borough Council DfT Road Maintenance Ratings 2025-2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">Scorecard</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Performance</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Rating</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200">
                <tr className="bg-emerald-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall</td>
                  <td className="px-6 py-4 text-center text-surface-600">—</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Condition</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">Above DfT threshold</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">A Roads</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">85.5% good (vs ~69% national)</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">B &amp; C Roads</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">86.5% good (vs ~62% national)</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Unclassified Roads</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">69.5% good (DfT dataset)</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Spend</td>
                  <td className="px-6 py-4 text-center text-surface-600">~90% preventative maintenance</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Best Practice</td>
                  <td className="px-6 py-4 text-center text-surface-600">Asset management aligned</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
            <p className="text-emerald-800">
              <strong>What all-GREEN means:</strong> Rotherham is performing above average on every DfT scorecard —
              one of a handful of councils in England to achieve that. Their A-roads sit 16 percentage points above
              the national average; B&amp;C roads 24 points above. That reflects genuine investment: £51m in
              dedicated unclassified-road capital since 2015, with a further £12m commitment from 2024/25.
            </p>
          </div>

          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-amber-800">
              <strong>Context for your claim:</strong> GREEN measures network-level performance against other councils —
              not whether the specific pothole that damaged your vehicle was reasonably inspected and repaired. Rotherham
              still filled an estimated 92,466 potholes across five years. A good rating makes generic "the roads are
              terrible" arguments useless. Specificity wins.
            </p>
          </div>
        </div>
      </section>

      {/* Network scale */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            1,191 Kilometres — Mostly Estate Roads
          </h2>
          <p className="text-surface-600 mb-8">
            Network size from Rotherham's own transparency report — where pothole claims actually happen
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Length</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Share of network</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A roads (principal)</td>
                  <td className="px-6 py-4 text-center font-mono">136.7 km</td>
                  <td className="px-6 py-4 text-center">11.5%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B &amp; C roads (non-principal)</td>
                  <td className="px-6 py-4 text-center font-mono">279.3 km</td>
                  <td className="px-6 py-4 text-center">23.4%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-medium text-amber-900">Unclassified (U roads)</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">775.0 km</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-700">65.1%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-bold text-brand-900">Total carriageway</td>
                  <td className="px-6 py-4 text-center font-mono font-bold">1,191.0 km</td>
                  <td className="px-6 py-4 text-center">100%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 text-brand-900">Footways</td>
                  <td className="px-6 py-4 text-center font-mono">1,666.4 km</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">A £1.93 Billion Asset</h3>
              <p className="text-surface-600 text-sm">
                Rotherham calculates gross replacement cost at over £1.93 billion — the largest financial asset
                the council manages. Their Highway Asset Management Plan, Code of Practice for Highway Inspection
                and Assessment, and certified National Register of Highway Inspectors underpin the best-practice
                GREEN scorecard.
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">Targets Beaten — April 2025</h3>
              <p className="text-emerald-800 text-sm">
                Council plan targets vs actual green-condition share: Principal roads target 72%, actual{' '}
                <strong>81.05%</strong>; non-principal target 66%, actual <strong>78.46%</strong>; unclassified
                target 60%, actual <strong>66.98%</strong>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five Years of Condition Data
          </h2>
          <p className="text-surface-600 mb-8">
            RED / amber / green percentages from Rotherham's transparency report — SCANNER on classified roads,
            coarse visual inspection on unclassified
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (136.7km — SCANNER, two-year cycle): stable but RED edging up</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">1.84%</td>
                    <td className="px-4 py-2 text-center">17.16%</td>
                    <td className="px-4 py-2 text-center">81.00%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">2.41%</td>
                    <td className="px-4 py-2 text-center">18.22%</td>
                    <td className="px-4 py-2 text-center">79.37%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">2.67%</td>
                    <td className="px-4 py-2 text-center font-bold">16.28%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">81.05%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road green share recovered to 81.05%, but RED condition has risen 45% since 2020/21 (1.84% → 2.67%).
              At 136.7km, that is still a small absolute area — but the trend matters if your claim is on a principal route.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (279.3km — SCANNER, two-year cycle): recovered from 2021/22 dip</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center">2.49%</td>
                    <td className="px-4 py-2 text-center">18.89%</td>
                    <td className="px-4 py-2 text-center">78.62%</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-amber-50">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">3.28%</td>
                    <td className="px-4 py-2 text-center font-bold">21.45%</td>
                    <td className="px-4 py-2 text-center font-bold">75.28%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">2.34%</td>
                    <td className="px-4 py-2 text-center">19.11%</td>
                    <td className="px-4 py-2 text-center">78.55%</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold">2.56%</td>
                    <td className="px-4 py-2 text-center font-bold">18.98%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">78.46%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B&amp;C green condition sits at 78.46% — well above the council's 66% target and the DfT's ~62% national
              average. Roughly one in five B/C roads is amber — maintenance may be required soon.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
            <h3 className="font-semibold text-emerald-900 mb-4">Unclassified roads (775km — CVI, four-year cycle): the turnaround story</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-emerald-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-emerald-100 border-b border-emerald-200">
                    <th className="px-4 py-2 text-left font-semibold text-emerald-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2020/21</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">18.89%</td>
                    <td className="px-4 py-2 text-center">26.02%</td>
                    <td className="px-4 py-2 text-center">55.09%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">14.28%</td>
                    <td className="px-4 py-2 text-center">21.51%</td>
                    <td className="px-4 py-2 text-center">64.21%</td>
                  </tr>
                  <tr className="border-b border-emerald-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">12.69%</td>
                    <td className="px-4 py-2 text-center">23.10%</td>
                    <td className="px-4 py-2 text-center">64.21%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-700">10.93%</td>
                    <td className="px-4 py-2 text-center font-bold">22.09%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">66.98%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-emerald-800 font-medium">
              RED share on estate roads nearly halved in five years (18.89% → 10.93%). The council also reports the
              unclassified network peaked at 24% in 2015 before capital investment began. But 10.93% RED on 775km
              still means substantial estate-road mileage in poor condition — and 22.09% amber means maintenance
              may be required soon on a further 171km.
            </p>
          </div>
        </div>
      </section>

      {/* Four-year CVI cycle — Section 58 */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Four-Year Estate-Road Survey Cycle
          </h2>
          <p className="text-surface-600 mb-8">
            How Rotherham inspects the 65% of its network where most pothole claims originate
          </p>

          <QuoteCard
            quote="Rotherham Council collects condition data of 100% our Unclassified Road Network (Estate Type Roads) through Coarse Visual Inspections over a 4-year cycle. The Coarse Visual Inspection (CVI) is usually carried out on foot and allows 25% (or a return period of once every four years) of the Unclassified Network (approximately 195km) to be assessed each year."
            source="Rotherham Metropolitan Borough Council Local Highways Maintenance Transparency Report 2025"
          />

          <div className="grid md:grid-cols-2 gap-6 mt-8 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Safety Inspections vs Condition Surveys</h3>
              <p className="text-surface-600 text-sm mb-3">
                Rotherham runs safety highway inspections under its Code of Practice — certified inspectors on the
                National Register of Highway Inspectors, recording defects that present immediate danger. That is
                separate from the four-year CVI condition survey covering 100% of U-roads on a rolling 25% per year basis.
              </p>
              <p className="text-surface-600 text-sm">
                Section 58 turns on the <strong>specific defect</strong> and the council's inspection regime for that
                road — not on whether the borough beat its green-condition targets.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">PAS 2161 From 2026/27</h3>
              <p className="text-amber-800 text-sm">
                The council notes that from 2026/27, road condition will be assessed under BSI PAS 2161 — five
                categories instead of three. Today's RED/amber/green percentages may not be directly comparable
                with post-2026 data. Build your claim on contemporaneous inspection records and defect criteria,
                not scorecard colours alone.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 58 Questions For Estate Roads</h3>
            <p className="text-brand-200 mb-4">
              Under Section 58 of the Highways Act 1980, Rotherham must show reasonable maintenance of the{' '}
              <strong className="text-white">specific highway</strong> where you were damaged — not just that the
              borough is all-GREEN. Ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last coarse-visually inspected — year one, two, three, or four of the cycle?</li>
              <li>• Did the pothole meet the council's Code of Practice intervention criteria for depth and width?</li>
              <li>• Was it reported before your incident — FixMyStreet, council online forms, ward member referral?</li>
              <li>• Was it on a section with repetitive reactive repairs flagged in the prioritisation matrix?</li>
              <li>• If 10.93% of U-roads were RED at the last survey, what was done about defects on yours?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot rely on network-level GREEN ratings to defeat a claim about a specific, reportable
              defect it failed to repair within its own Code of Practice timeframes.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            92,466 Estimated Pothole Repairs in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes from the council's transparency report — the counterweight to a GREEN rating
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Estimated potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Km of road repaired</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">19,585</td>
                  <td className="px-6 py-4 text-center">79.68</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">20,021</td>
                  <td className="px-6 py-4 text-center">78.63</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">16,758</td>
                  <td className="px-6 py-4 text-center">60.87</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">20,166</td>
                  <td className="px-6 py-4 text-center">50.36</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">15,936</td>
                  <td className="px-6 py-4 text-center">49.00</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">92,466</td>
                  <td className="px-6 py-4 text-center font-bold">318.54</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~51 Repairs a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Rotherham fills an estimated 51 potholes per day. Planned km repaired
                has fallen from 79.68km (2020/21) to 49km (2024/25) as the council shifts toward surface treatments
                and timely intervention — but reactive volume remains high. Defects still form between inspections.
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">The Council's Own Framing</h3>
              <QuoteCard
                quote="Balancing repairs across the different highway conditions helps to provide more control of the need for 'reactive / pothole' repairs. Safety is always our priority, and dangerous defects are always given a high priority."
                source="Rotherham Metropolitan Borough Council Local Highways Maintenance Transparency Report 2025"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">Following the Money</h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            Rotherham supplements DfT grants with substantial council capital — and keeps reactive spend below 11%
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 p-6 mb-8 overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">Rotherham highway maintenance spending 2020-2026</caption>
              <thead>
                <tr className="border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Council capital</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative %</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">£4.0m CRSTS + £2.3m Pothole Fund</td>
                  <td className="px-4 py-3 text-center">£6.0m</td>
                  <td className="px-4 py-3 text-center">£749.61k</td>
                  <td className="px-4 py-3 text-center text-emerald-400">93.91%</td>
                  <td className="px-4 py-3 text-center">6.09%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">£4.0m CRSTS</td>
                  <td className="px-4 py-3 text-center">£6.0m</td>
                  <td className="px-4 py-3 text-center">£673.30k</td>
                  <td className="px-4 py-3 text-center text-emerald-400">93.27%</td>
                  <td className="px-4 py-3 text-center">6.73%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">£4.0m CRSTS + £1.1m NNRRF</td>
                  <td className="px-4 py-3 text-center">£3.0m CW + £1.2m FW</td>
                  <td className="px-4 py-3 text-center">£964.69k</td>
                  <td className="px-4 py-3 text-center text-emerald-400">89.63%</td>
                  <td className="px-4 py-3 text-center">10.37%</td>
                </tr>
                <tr className="bg-brand-800/50">
                  <td className="px-4 py-3 font-medium">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center">£4.0m CRSTS + £1.961m LHMF</td>
                  <td className="px-4 py-3 text-center">£3.0m CW + £1.2m FW</td>
                  <td className="px-4 py-3 text-center">£952.25k</td>
                  <td className="px-4 py-3 text-center text-emerald-400">90.63%</td>
                  <td className="px-4 py-3 text-center">9.37%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-white mb-3">£51m+ Into Estate Roads Since 2015</h3>
              <p className="text-brand-200 text-sm">
                £5m (two years) + £10m (three years) + £24m (four years from 2020/21) + £12m (four years from
                2024/25) in dedicated unclassified-network capital. The council reports this arrested deterioration
                and cut RED condition from a 2015 peak of 24% to 11% in March 2025.
              </p>
            </div>
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30">
              <h3 className="font-semibold text-white mb-3">2025/26 Programme</h3>
              <p className="text-brand-200 text-sm">
                Planned repairs to over <strong className="text-white">187 roads</strong> (~48km),{' '}
                <strong className="text-white">95 footways</strong> (~23km), and 35 pairs of tactile dropped
                crossings — via surface dressing, micro asphalt, resurfacing, and constructional repair.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Rotherham's Report Acknowledges
          </h2>
          <p className="text-surface-600 mb-8">
            Verbatim from the 2025 Local Highways Maintenance Transparency Report
          </p>

          <div className="space-y-6">
            <QuoteCard
              quote="Investment, Collaborative working and good Asset management principles has seen an improvement in the condition of the Unclassified Network from its peak at 24% in 2015 to 11% in March 2025. (Lower being better)."
              source="Rotherham Metropolitan Borough Council Local Highways Maintenance Transparency Report 2025"
            />

            <QuoteCard
              quote="Through timely intervention and good asset management principles, Rotherham Council have moved away from 'worst first' planning to using resources to stem the decline of assets in better condition whilst maintaining the condition of the poorest condition assets."
              source="Rotherham Metropolitan Borough Council Local Highways Maintenance Transparency Report 2025"
            />

            <QuoteCard
              quote="The Council committed to invest into the Unclassified Road Network in the 2015/2026 financial year with £5m over a two-year period, this Investment arrested the deterioration during this period. A further investment of £10m over three years followed by £24m over four years enabled the Highway Asset team, through good asset management principles, improve the condition by nearly 50% of its peak in 2015."
              source="Rotherham Metropolitan Borough Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>
        </div>
      </section>

      {/* Balanced assessment */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an All-GREEN Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Rotherham is not Derbyshire — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> All four DfT scorecards GREEN — among England's best-rated authorities
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 89–94% preventative spend across five published years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road RED share nearly halved since 2020/21 (18.89% → 10.93%)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Certified highway inspectors, documented Code of Practice, HAMP-aligned asset management
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £51m+ capital invested in estate roads since 2015
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect a well-documented Section 58 defence. Generic "council neglect" arguments will fail.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 15,936 estimated pothole repairs in 2024/25 — defects still form daily
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 10.93% of 775km U-roads still RED — plus 22.09% amber
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road condition surveys on a four-year CVI cycle (25% per year)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> A-road RED condition up 45% since 2020/21 (1.84% → 2.67%)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Prioritisation weighs ward member nominations and customer reports — proof of prior reporting matters
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with all-GREEN scorecards and 90%+ preventative spend, your claim lives or dies on
              the <strong className="text-white">specific defect</strong> under Section 41 and Section 58:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual or constructive notice</li>
              <li>• Photos showing defect size, depth, and visible age (weathered edges, previous patching)</li>
              <li>• Whether the pothole met the council's Code of Practice intervention criteria</li>
              <li>• Road class — on a U-road, the four-year CVI survey cycle and 10.93% RED share are structural context</li>
              <li>• Repetitive reactive repairs on the same section — flagged in the council's own prioritisation matrix</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and Rotherham's own transparency
              data cited where it helps — without pretending an all-GREEN council is the same as a failing one.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Rotherham?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            An all-GREEN council demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• Ignore all-GREEN Section 58 strength</li>
                <li>• No four-year CVI cycle argument</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ All-GREEN rating acknowledged honestly</li>
                <li>✅ 92,466 five-year pothole total cited</li>
                <li>✅ U-road four-year survey cycle argued</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Rotherham</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=rotherham"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Rotherham Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {FAQ_ITEMS.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.rotherham.gov.uk/roads-pavements/local-highways-maintenance-funding"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Rotherham Metropolitan Borough Council Local Highways Maintenance Transparency Report 2025
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
